import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Check, Gift, Calendar, AlertCircle, Loader } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MembershipPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name || !formData.customer_email || !formData.customer_phone) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/subscriptions/checkout`, formData);
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4">
        <div className="absolute inset-0 bg-gradient-to-b from-yellow-400/5 to-transparent"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <Crown className="w-16 h-16 text-yellow-400 mx-auto mb-6" />
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Become an <span className="text-yellow-400">Elite Member</span>
          </h1>
          <p className="text-xl text-gray-300 mb-8">
            Save money with our exclusive 3-month membership plan
          </p>
        </div>
      </section>

      {/* Membership Plan */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="bg-gradient-to-br from-yellow-400/10 to-yellow-600/5 border-2 border-yellow-400 rounded-3xl p-8 md:p-12 relative overflow-hidden">
            {/* Premium Badge */}
            <div className="absolute top-6 right-6 bg-yellow-400 text-black px-6 py-2 rounded-full font-bold text-sm">
              PREMIUM PLAN
            </div>

            <div className="grid md:grid-cols-2 gap-12">
              {/* Left Side - Plan Details */}
              <div>
                <h2 className="text-4xl font-bold text-white mb-4">
                  3-Month Elite Membership
                </h2>
                <div className="flex items-baseline mb-8">
                  <span className="text-6xl font-bold text-yellow-400">$450</span>
                  <span className="text-gray-400 ml-3 text-xl">/3 months</span>
                </div>

                {/* What's Included */}
                <div className="space-y-4 mb-8">
                  <h3 className="text-xl font-bold text-white mb-4">What's Included:</h3>
                  
                  <div className="flex items-start space-x-3">
                    <Check className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">3 IV Treatments</p>
                      <p className="text-gray-400 text-sm">One premium IV cocktail per month - choose from our full menu</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <Gift className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">1 FREE IM Injection</p>
                      <p className="text-gray-400 text-sm">Choose any intramuscular injection from our add-ons menu</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <Calendar className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">Flexible Scheduling</p>
                      <p className="text-gray-400 text-sm">Book your appointments at your convenience</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <Crown className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">VIP Treatment</p>
                      <p className="text-gray-400 text-sm">Priority booking and exclusive member benefits</p>
                    </div>
                  </div>
                </div>

                {/* Terms */}
                <div className="bg-black/30 rounded-xl p-4 border border-yellow-400/20">
                  <p className="text-yellow-400 font-semibold text-sm mb-2">Important Terms:</p>
                  <ul className="text-gray-400 text-xs space-y-1">
                    <li>• Membership valid for 90 days from purchase date</li>
                    <li>• Use 1 treatment per month for optimal wellness</li>
                    <li>• Unused treatments are non-refundable</li>
                    <li>• Cannot be combined with other promotions</li>
                  </ul>
                </div>
              </div>

              {/* Right Side - Sign Up Form */}
              <div>
                <div className="bg-black/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-800">
                  <h3 className="text-2xl font-bold text-white mb-6">Become a Member</h3>
                  
                  {error && (
                    <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-start space-x-3">
                      <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <p className="text-red-400 text-sm">{error}</p>
                    </div>
                  )}

                  <form onSubmit={handleSubscribe} className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="customer_name"
                        value={formData.customer_name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                        placeholder="John Doe"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="customer_email"
                        value={formData.customer_email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                        placeholder="john@example.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        name="customer_phone"
                        value={formData.customer_phone}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                        placeholder="(727) 555-0123"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-black font-bold py-4 px-6 rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                    >
                      {loading ? (
                        <>
                          <Loader className="w-5 h-5 animate-spin" />
                          <span>Processing...</span>
                        </>
                      ) : (
                        <>
                          <Crown className="w-5 h-5" />
                          <span>Subscribe Now - $450</span>
                        </>
                      )}
                    </button>

                    <p className="text-xs text-gray-400 text-center">
                      Secure payment processed by Stripe. One-time payment of $450.
                    </p>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4 bg-gradient-to-b from-transparent to-yellow-400/5">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-white mb-12">
            Why Choose Elite Membership?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <div className="w-12 h-12 bg-yellow-400/10 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl">💰</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Save Money</h3>
              <p className="text-gray-400">
                Pay only $150 per treatment instead of regular pricing. That's significant savings over 3 months!
              </p>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <div className="w-12 h-12 bg-yellow-400/10 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl">📅</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Consistent Care</h3>
              <p className="text-gray-400">
                Regular monthly treatments help maintain optimal wellness and health benefits.
              </p>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <div className="w-12 h-12 bg-yellow-400/10 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl">🎁</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Bonus Gift</h3>
              <p className="text-gray-400">
                Get a FREE IM injection of your choice - valued at up to $40!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-white mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <h3 className="text-lg font-bold text-yellow-400 mb-2">
                When does my membership start?
              </h3>
              <p className="text-gray-300">
                Your 3-month membership begins immediately upon payment and ends exactly 90 days later.
              </p>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <h3 className="text-lg font-bold text-yellow-400 mb-2">
                Can I use all 3 treatments at once?
              </h3>
              <p className="text-gray-300">
                While we recommend spacing treatments monthly for optimal health benefits, you can schedule them as needed within your 90-day period.
              </p>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <h3 className="text-lg font-bold text-yellow-400 mb-2">
                What if I don't use all my treatments?
              </h3>
              <p className="text-gray-300">
                Unused treatments cannot be refunded or extended beyond the 90-day period. We encourage you to utilize your full membership benefits.
              </p>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <h3 className="text-lg font-bold text-yellow-400 mb-2">
                Which IV treatments can I choose?
              </h3>
              <p className="text-gray-300">
                You can select any IV cocktail from our full menu for each treatment - Myers' Cocktail, Hydration, Athletic Performance, Immunity Boost, and more!
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MembershipPage;
